"use client";

import React, { useEffect } from "react";
import { Phone, Mail, MoreHorizontal } from "lucide-react";
import { ButtonIcon } from "@/components/common/StandardButton";
import StandardCard from "../common/StandardCard";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { orderActions } from "@/store/actions/order.actions";

interface CustomerInformationProps {
  orderId: string;
}

const CustomerInformation: React.FC<CustomerInformationProps> = ({
  orderId,
}) => {
  const dispatch = useAppDispatch();
  const { selectedOrder, detailsLoading, error } = useAppSelector(
    (state) => state.order,
  );

  // No need to fetch here - parent component handles it

  if (detailsLoading) {
    return (
      <StandardCard>
        <div className="space-y-4 animate-pulse">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 bg-muted rounded-full"></div>
            <div className="flex-1">
              <div className="h-4 bg-muted rounded w-32 mb-2"></div>
              <div className="h-3 bg-muted rounded w-48"></div>
            </div>
          </div>
          <div className="space-y-3">
            <div className="h-4 bg-muted rounded w-24 mb-2"></div>
            <div className="h-3 bg-muted rounded w-36"></div>
            <div className="h-3 bg-muted rounded w-40"></div>
            <div className="h-3 bg-muted rounded w-44"></div>
          </div>
        </div>
      </StandardCard>
    );
  }

  if (error || !selectedOrder?.customer) {
    return (
      <StandardCard>
        <div className="text-center text-muted-foreground">
          {error || "No customer information available"}
        </div>
      </StandardCard>
    );
  }

  const { customer } = selectedOrder;
  const customerName =
    `${customer.first_name || ""} ${customer.last_name || ""}`.trim() || "N/A";
  const shippingAddress = customer?.customer_addresses?.find(
    (addr) => !addr.billing_address,
  );
  const billingAddress = customer?.customer_addresses?.find(
    (addr) => addr.billing_address,
  );

  return (
    <StandardCard>
      <div className="space-y-4">
        {/* Customer Profile */}
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <h3 className="font-medium text-foreground">{customerName}</h3>
            <p className="text-sm text-muted-foreground">
              {customer.email || "No email"}
            </p>
          </div>
          <ButtonIcon variant="ghost">
            <MoreHorizontal className="h-4 w-4" />
          </ButtonIcon>
        </div>

        {/* Customer Information Section */}
        <div>
          <h4 className="text-sm font-medium text-primary mb-3 uppercase tracking-wider">
            Customer Information
          </h4>
          <div className="space-y-3">
            {customer.phone_number && (
              <div className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-foreground">
                  {customer.phone_number}
                </span>
              </div>
            )}
            {customer.email && (
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-foreground">
                  {customer.email}
                </span>
              </div>
            )}
            {customer.total_orders && (
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground">
                  Total Orders:
                </span>
                <span className="text-sm text-foreground">
                  {customer.total_orders}
                </span>
              </div>
            )}
            {customer.total_spent && (
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground">
                  Total Spent:
                </span>
                <span className="text-sm text-foreground">
                  ₹{(customer.total_spent / 100).toLocaleString()}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Shipping Address */}
        {shippingAddress && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-primary uppercase tracking-wider">
                Shipping Address
              </h4>
              <ButtonIcon variant="ghost">
                <MoreHorizontal className="h-4 w-4" />
              </ButtonIcon>
            </div>
            <p className="text-sm text-foreground leading-relaxed">
              {shippingAddress.address_line_one}
              {shippingAddress.address_line_two &&
                `, ${shippingAddress.address_line_two}`}
              <br />
              {shippingAddress.city}, {shippingAddress.state} -{" "}
              {shippingAddress.postal_code}
            </p>
          </div>
        )}

        {/* Billing Address */}
        {billingAddress && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-primary uppercase tracking-wider">
                Billing Address
              </h4>
              <ButtonIcon variant="ghost">
                <MoreHorizontal className="h-4 w-4" />
              </ButtonIcon>
            </div>
            <p className="text-sm text-foreground leading-relaxed">
              {billingAddress.address_line_one}
              {billingAddress.address_line_two &&
                `, ${billingAddress.address_line_two}`}
              <br />
              {billingAddress.city}, {billingAddress.state} -{" "}
              {billingAddress.postal_code}
            </p>
          </div>
        )}
      </div>
    </StandardCard>
  );
};

export default CustomerInformation;
